/**
 * Which of `SIMEND.DAT`'s eleven sequences a sortie earned, and its prose with
 * the original's three substitutions filled in.
 *
 * The original answers *"how did you come down?"* before it answers *"how did
 * it go?"* — the outcome sequence, then MISSION DEBRIEFING
 * (`docs/game/end-of-mission.md`). The port showed one hardcoded
 * `SAFE RETURN...` line on `/career` and nothing at all on `/fly` until this
 * module; the prose is `simend-text.ts` and the screens are `apps/web`'s
 * `simend-screens.ts`.
 *
 * ## Which sequence, and on what evidence
 *
 * `PS.EXE`'s selection code has **not** been disassembled — the same standing
 * as `debrief-review.ts`'s message rules, and the rules below are the same kind
 * of thing: readings of each sequence's own wording, which states its condition
 * plainly (*"You've crashed in enemy territory!"*, *"You land safely... in
 * enemy territory!"*, *"You've been wounded in the air"*). Treat them as
 * INFERRED (strong). Two of them are weaker than that and say so at their rule.
 *
 * The manual supplies the shape the rules are hung on, and it is the same one
 * `campaign/casualty.ts` already reads: *"If you crash, you may either walk
 * away with no injuries, end up in a hospital, or be killed… If you land or
 * crash (and survive) in enemy territory, you may end up in an enemy
 * prisoner-of-war camp"*. So a career sortie's `CasualtyKind` — the campaign's
 * own roll — decides the **family**, and the landing predicates decide which
 * member of it. A single mission has no career and therefore no roll, and then
 * the predicates decide both.
 *
 * ## Two sequences this port cannot yet reach, and why
 *
 * - **`vanquishedByAce`** (`"%a has shot you down."`) needs the name of
 *   whoever shot you down. The sim knows it one layer down —
 *   `CombatAircraft.lastAttackerId` is set on the player's own entry — but
 *   nothing carries it as far as `MissionDebrief`, so `killedBy` is never
 *   supplied and the plain `vanquished` is chosen instead. Wiring it is a
 *   mirror of `apply-debrief.ts`'s `creditClaims`, which already matches a
 *   callsign against `FAMOUS_ACES`.
 * - **`safeReturnNeighbour`** (*"You land at a neighboring aerodrome"*) is the
 *   genuinely ambiguous sibling of `safeLanding` (*"You land safely and make it
 *   back to your aerodrome"*). Both are *down in one piece on our side, away
 *   from home*, and nothing in the port distinguishes a field from another
 *   squadron's aerodrome. `safeLanding` is chosen for the whole case — the same
 *   call `debrief-review.ts` makes on HUNT A ZEPPELIN's outstanding/success
 *   split, and recorded the same way rather than being guessed at.
 *
 * ## A bailed pilot needs no twelfth sequence (AD-021)
 *
 * The port added a bail-out the original does not have, and the first instinct
 * was that it would need new prose. It does not, and this is worth stating
 * plainly because writing a new screen here would have put port-authored
 * sentences beside `SIMEND.DAT`'s own on the same panel.
 *
 * **A man who comes down under a canopy is a man who landed safely.** Read the
 * three sequences he can reach with that in mind and they are already about
 * him:
 *
 * | where he lands | sequence | its own words |
 * |---|---|---|
 * | our side, unhurt | `SAFE LANDING` | *"You land safely and make it back to your aerodrome."* |
 * | our side, wounded | `INJURED` | *"You've been wounded in the air by enemy fire. After managing to land safely…"* |
 * | their side | `BEHIND THE LINES` | *"You land safely... in enemy territory! Enemy soldiers capture you immediately…"* |
 *
 * Not one of those sentences mentions an aeroplane, and `BEHIND THE LINES` is a
 * better description of a captured parachutist than `CAPTURED!` is — that one
 * has enemy soldiers pulling him *from the wreck*, which is the thing he is not
 * in. The aeroplane's fate is reported where it belongs: the debriefing's
 * AIRCRAFT LOST well, which counts it either way.
 *
 * The mechanism is one qualifier on `wrecked` and one widening of
 * `landedSafely` into `downInOnePiece`; see `simendOutcome`.
 */

import { SIMEND_SEQUENCES, type SimendTextPage, type SimendTextSequence } from "./simend-text.ts";

/**
 * The eleven outcomes, **in `SIMEND.DAT`'s own order** — the index into
 * `SIMEND_SEQUENCES` is the position in this list, and `simendSequenceOf`
 * relies on that.
 */
export const SIMEND_OUTCOMES = [
  "crashedUnhurt",
  "crashedHospital",
  "captured",
  "crashedFatal",
  "vanquishedByAce",
  "vanquished",
  "safeReturnBase",
  "safeReturnNeighbour",
  "behindTheLines",
  "safeLanding",
  "injured",
] as const;

export type SimendOutcome = (typeof SIMEND_OUTCOMES)[number];

/**
 * The campaign's casualty verdict, when there is a career behind the sortie.
 * Structurally `campaign/casualty.ts`'s `CasualtyKind`, restated here so the
 * mission layer does not import the campaign layer; `simend.test.ts` asserts
 * the two lists are the same.
 */
export type SimendCasualty = "unhurt" | "hospital" | "captured" | "killed";

/**
 * Everything the choice is made from. All eight predicates are `MissionDebrief`
 * fields of the same name; the last three are what a career adds.
 */
export interface SimendSituation {
  /** The aeroplane was destroyed. */
  readonly playerLost: boolean;
  readonly pilotKilled: boolean;
  readonly playerWounded: boolean;
  readonly landedSafely: boolean;
  readonly landedAtBase: boolean;
  readonly overFriendlyTerritory: boolean;
  /** Down in one piece, but the undercarriage went — the machine is a write-off. */
  readonly gearCollapsed: boolean;
  readonly landedInEnemyTerritory: boolean;
  /** Rounds that hit the player's aeroplane. Separates *shot down* from *flew into the ground*. */
  readonly hitsTaken: number;
  /** The campaign's own roll, when a career flew this sortie. */
  readonly casualty?: SimendCasualty;
  /** The pilot who shot you down, when the sim can name him. Never supplied yet — see the module note. */
  readonly killedBy?: string;
  /**
   * He took to the silk (AD-021). Optional so every existing caller and every
   * existing test means `false` by saying nothing.
   *
   * **This adds no twelfth sequence.** See `downInOnePiece` below for why the
   * original's own eleven already cover a parachutist, and
   * `docs/engineering/bail-out-design.md` §the-outcome for the whole argument.
   */
  readonly baledOut?: boolean;
}

/**
 * Dead, and the aeroplane was shot out from under him rather than flown into
 * the ground. `hitsTaken` is the discriminator the port has: `VANQUISHED!`
 * says *"You've been shot down"* and `CRASHED!!!` says *"You don't walk away
 * from this wreck"*, and nothing else in `MissionDebrief` separates a pilot
 * killed by enemy fire from one killed by the ground. INFERRED.
 */
function shotDown(situation: SimendSituation): boolean {
  return situation.hitsTaken > 0;
}

/**
 * Chooses one of the eleven. **Total** — every combination of the predicates
 * reaches a named outcome, which `simend.test.ts` asserts by walking all 512
 * boolean combinations twice over (with and without each casualty verdict).
 *
 * The order of the tests is the order of consequence the manual lists: killed,
 * then captured, then wrecked, then hurt, then home.
 */
export function simendOutcome(situation: SimendSituation): SimendOutcome {
  if (situation.pilotKilled || situation.casualty === "killed") {
    if (!shotDown(situation)) return "crashedFatal";
    return situation.killedBy ? "vanquishedByAce" : "vanquished";
  }

  // A write-off *with the pilot in it*: destroyed in the air, or put down so
  // hard the machine is gone.
  //
  // AD-021 is the case that made the qualifier necessary. `wrecked`'s job in
  // this selector has always been "did he ride it down?" — `CRASHED!` opens
  // *"You've crashed! You walk away from the wreck"* and `CAPTURED!` opens
  // *"Enemy soldiers pull you from the wreck"*, and both sentences are about a
  // man who was in the aeroplane. A pilot who jumped was not. His aeroplane is
  // still lost, and the debriefing still counts it under AIRCRAFT LOST; this
  // screen is about him.
  const wrecked = !situation.baledOut && (situation.playerLost || situation.gearCollapsed);
  // Down on his feet in one piece — under an undercarriage or under a canopy.
  const downInOnePiece = situation.landedSafely || (situation.baledOut === true && !situation.pilotKilled);
  // The wrong side of the wire. `landedInEnemyTerritory` is the manual's
  // *"land … in enemy territory"* half; `!overFriendlyTerritory` covers the
  // crash there, and a career's own `"captured"` roll overrides both.
  const behindTheLines =
    situation.casualty === "captured" || situation.landedInEnemyTerritory || !situation.overFriendlyTerritory;
  const hurt = situation.casualty === "hospital" || situation.playerWounded;

  if (behindTheLines) {
    // `CAPTURED!` opens *"You've crashed in enemy territory!"*; `BEHIND THE
    // LINES` opens *"You land safely... in enemy territory!"*. The machine is
    // what separates them, not the pilot.
    return wrecked || !downInOnePiece ? "captured" : "behindTheLines";
  }
  if (wrecked) return hurt ? "crashedHospital" : "crashedUnhurt";
  if (hurt) return "injured";
  // Down on our side, away from home. See the module note on the one sequence
  // this case cannot tell apart.
  if (downInOnePiece && !situation.landedAtBase) return "safeLanding";
  // Landed at his own aerodrome — or never landed at all, which the original
  // has no screen for because its sim only ends on the ground. A sortie that
  // `/fly` ends in the air is reported as the flight home it would have been.
  return "safeReturnBase";
}

export interface SimendFacts {
  /**
   * How many months `%t` stands for — the hospital or prison-camp stay. A
   * career supplies it from `CasualtyReport.lostDays`; a single mission has no
   * convalescence to report and takes the floor below.
   */
  readonly months?: number;
  /** `%a`. */
  readonly killedBy?: string;
}

/** The shortest stay the original's own wording admits: `PS.EXE` carries the string `" month"`, singular. */
export const SIMEND_MIN_MONTHS = 1;

/** *"3 months"* / *"1 month"* — the noun is `PS.EXE`'s, sitting in `simend.dat`'s own string block. */
export function simendMonths(months: number): string {
  const whole = Math.max(SIMEND_MIN_MONTHS, Math.round(months));
  return whole === 1 ? "1 month" : `${whole} months`;
}

function expand(line: string, facts: SimendFacts): string {
  return line
    .replaceAll("%t", simendMonths(facts.months ?? SIMEND_MIN_MONTHS))
    .replaceAll("%a", facts.killedBy ?? "An enemy pilot");
}

function expandPage(page: SimendTextPage, facts: SimendFacts): SimendTextPage {
  return { ...page, lines: page.lines.map((line) => expand(line, facts)) };
}

/** The chosen sequence with `%t` and `%a` filled in. `$` is left in — it is a *pause*, and the screen owns it. */
export function simendSequenceOf(outcome: SimendOutcome, facts: SimendFacts = {}): SimendTextSequence {
  const index = SIMEND_OUTCOMES.indexOf(outcome);
  const sequence = SIMEND_SEQUENCES[index];
  if (!sequence) throw new Error(`simend: no sequence for outcome ${outcome}`);
  return { title: sequence.title, pages: sequence.pages.map((page) => expandPage(page, facts)) };
}
